import { useState, useRef, useCallback, useEffect } from 'react';

export interface UseGroupWebRTCOptions {
  myUserId: string;
  onSignal: (targetUserId: string, signalType: 'OFFER' | 'ANSWER' | 'ICE_CANDIDATE', payload: any) => void;
  iceServers?: RTCIceServer[];
}

export function useGroupWebRTC({ myUserId, onSignal, iceServers }: UseGroupWebRTCOptions) {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  // Map<remoteUserId, RTCPeerConnection>
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const localStreamRef = useRef<MediaStream | null>(null);
  const originalCameraTrackRef = useRef<MediaStreamTrack | null>(null);
  const pendingIceCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());

  // Perfect Negotiation state per remote peer: Map<remoteUserId, boolean>
  const makingOfferMapRef = useRef<Map<string, boolean>>(new Map());
  const isSettingRemoteAnswerPendingMapRef = useRef<Map<string, boolean>>(new Map());

  const onSignalRef = useRef(onSignal);
  onSignalRef.current = onSignal;

  const iceServersRef = useRef(iceServers);
  iceServersRef.current = iceServers;

  const cleanupPeerConnection = useCallback((remoteUserId: string) => {
    const pc = peerConnectionsRef.current.get(remoteUserId);
    if (pc) {
      pc.ontrack = null;
      pc.onicecandidate = null;
      pc.onconnectionstatechange = null;
      pc.onnegotiationneeded = null;
      pc.close();
      peerConnectionsRef.current.delete(remoteUserId);
    }
    makingOfferMapRef.current.delete(remoteUserId);
    isSettingRemoteAnswerPendingMapRef.current.delete(remoteUserId);
    pendingIceCandidatesRef.current.delete(remoteUserId);

    setRemoteStreams((prev) => {
      const next = new Map(prev);
      next.delete(remoteUserId);
      return next;
    });
  }, []);

  const cleanupAll = useCallback(() => {
    peerConnectionsRef.current.forEach((pc) => {
      pc.ontrack = null;
      pc.onicecandidate = null;
      pc.onconnectionstatechange = null;
      pc.onnegotiationneeded = null;
      pc.close();
    });
    peerConnectionsRef.current.clear();
    makingOfferMapRef.current.clear();
    isSettingRemoteAnswerPendingMapRef.current.clear();
    pendingIceCandidatesRef.current.clear();

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    if (originalCameraTrackRef.current) {
      originalCameraTrackRef.current.stop();
      originalCameraTrackRef.current = null;
    }

    setLocalStream(null);
    setRemoteStreams(new Map());
    setScreenStream(null);
    setIsMuted(false);
    setIsCameraOff(false);
    setIsScreenSharing(false);
  }, []);

  // Flush buffered ICE candidates for a remote peer
  const flushPendingCandidates = useCallback(async (remoteUserId: string) => {
    const pc = peerConnectionsRef.current.get(remoteUserId);
    if (!pc || !pc.remoteDescription) return;

    const candidates = pendingIceCandidatesRef.current.get(remoteUserId) || [];
    pendingIceCandidatesRef.current.delete(remoteUserId);

    for (const candidate of candidates) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (err) {
        console.warn(`[GroupWebRTC] Failed to flush ICE candidate for ${remoteUserId}`, err);
      }
    }
  }, []);

  // Initialize local media stream
  const initLocalMedia = useCallback(async (callType: 'AUDIO' | 'VIDEO') => {
    if (localStreamRef.current) return localStreamRef.current;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: callType === 'VIDEO'
        ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }
        : false,
    });

    localStreamRef.current = stream;
    setLocalStream(stream);

    if (callType === 'VIDEO') {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) originalCameraTrackRef.current = videoTrack;
    }

    return stream;
  }, []);

  // Connect to a specific remote peer using Perfect Negotiation (Deterministic Role)
  const connectToPeer = useCallback(async (remoteUserId: string, stream: MediaStream) => {
    if (peerConnectionsRef.current.has(remoteUserId)) {
      return peerConnectionsRef.current.get(remoteUserId)!;
    }

    const isPolite = myUserId.localeCompare(remoteUserId) > 0;
    console.log(`[GroupWebRTC] Creating PeerConnection with ${remoteUserId} | isPolite: ${isPolite}`);

    const configuration: RTCConfiguration = {
      iceServers: iceServersRef.current || [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
    };

    const pc = new RTCPeerConnection(configuration);
    peerConnectionsRef.current.set(remoteUserId, pc);

    stream.getTracks().forEach((track) => {
      pc.addTrack(track, stream);
    });

    pc.ontrack = (event) => {
      console.log(`[GroupWebRTC] REMOTE_TRACK_RECEIVED from ${remoteUserId}: kind=${event.track.kind} readyState=${event.track.readyState} enabled=${event.track.enabled}`);
      let rStream: MediaStream;
      if (event.streams && event.streams[0]) {
        rStream = event.streams[0];
      } else {
        rStream = new MediaStream([event.track]);
      }

      event.track.onunmute = () => {
        console.log(`[GroupWebRTC] Remote track unmuted from ${remoteUserId}: kind=${event.track.kind}`);
        setRemoteStreams((prev) => new Map(prev).set(remoteUserId, rStream));
      };

      setRemoteStreams((prev) => new Map(prev).set(remoteUserId, rStream));
    };


    pc.onicecandidate = (event) => {
      if (event.candidate) {
        onSignalRef.current(remoteUserId, 'ICE_CANDIDATE', { candidate: event.candidate.toJSON() });
      }
    };

    pc.onconnectionstatechange = () => {
      console.log(`[GroupWebRTC] Connection state with ${remoteUserId}: ${pc.connectionState}`);
      if (pc.connectionState === 'failed') {
        cleanupPeerConnection(remoteUserId);
      }
    };

    pc.onnegotiationneeded = async () => {
      console.log(`[GroupWebRTC] onnegotiationneeded for peer ${remoteUserId} | isPolite: ${isPolite}`);
      try {
        makingOfferMapRef.current.set(remoteUserId, true);
        await pc.setLocalDescription();
        if (pc.localDescription) {
          onSignalRef.current(remoteUserId, 'OFFER', { sdp: pc.localDescription.sdp });
        }
      } catch (err) {
        console.error(`[GroupWebRTC] Offer error for peer ${remoteUserId}:`, err);
      } finally {
        makingOfferMapRef.current.set(remoteUserId, false);
      }
    };

    return pc;
  }, [myUserId, cleanupPeerConnection]);

  // Process incoming targeted WebRTC signals from remote peers
  const handleRemoteSignal = useCallback(async (senderId: string, signalType: string, payload: any) => {
    let pc = peerConnectionsRef.current.get(senderId);

    if (!pc) {
      if (!localStreamRef.current) return;
      pc = await connectToPeer(senderId, localStreamRef.current);
    }

    const isPolite = myUserId.localeCompare(senderId) > 0;
    const makingOffer = makingOfferMapRef.current.get(senderId) || false;
    const isSettingRemoteAnswerPending = isSettingRemoteAnswerPendingMapRef.current.get(senderId) || false;

    if (signalType === 'OFFER' && payload?.sdp) {
      const offerCollision = makingOffer || pc.signalingState !== 'stable' || isSettingRemoteAnswerPending;
      const ignoreOffer = !isPolite && offerCollision;

      if (ignoreOffer) {
        console.log(`[GroupWebRTC] Impolite peer ignoring offer collision from ${senderId}`);
        return;
      }

      if (offerCollision && isPolite) {
        console.log(`[GroupWebRTC] Polite peer rolling back offer for ${senderId}`);
        await pc.setLocalDescription({ type: 'rollback' });
        makingOfferMapRef.current.set(senderId, false);
      }

      isSettingRemoteAnswerPendingMapRef.current.set(senderId, true);
      await pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
      isSettingRemoteAnswerPendingMapRef.current.set(senderId, false);

      await pc.setLocalDescription();
      if (pc.localDescription) {
        onSignalRef.current(senderId, 'ANSWER', { sdp: pc.localDescription.sdp });
      }
      await flushPendingCandidates(senderId);

    } else if (signalType === 'ANSWER' && payload?.sdp) {
      console.log(`[GroupWebRTC] Setting remote ANSWER from ${senderId}`);
      await pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
      await flushPendingCandidates(senderId);

    } else if (signalType === 'ICE_CANDIDATE' && payload?.candidate) {
      if (pc.remoteDescription) {
        try {
          await pc.addIceCandidate(payload.candidate);
        } catch (err) {
          console.warn(`[GroupWebRTC] Failed to add ICE candidate from ${senderId}`, err);
        }
      } else {
        const buffered = pendingIceCandidatesRef.current.get(senderId) || [];
        buffered.push(payload.candidate);
        pendingIceCandidatesRef.current.set(senderId, buffered);
      }
    }
  }, [myUserId, connectToPeer, flushPendingCandidates]);

  // Media Controls
  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    const audioTrack = localStreamRef.current.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setIsMuted(!audioTrack.enabled);
    }
  }, []);

  const toggleCamera = useCallback(() => {
    if (!localStreamRef.current) return;
    const videoTrack = localStreamRef.current.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled;
      setIsCameraOff(!videoTrack.enabled);
    }
  }, []);

  const toggleScreenShare = useCallback(async () => {
    if (isScreenSharing) {
      for (const pc of peerConnectionsRef.current.values()) {
        const videoSender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (videoSender && originalCameraTrackRef.current) {
          await videoSender.replaceTrack(originalCameraTrackRef.current);
        }
      }
      setIsScreenSharing(false);
      setScreenStream(null);
    } else {
      try {
        const capturedScreenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
        const screenTrack = capturedScreenStream.getVideoTracks()[0];
        if (!screenTrack) return;

        for (const pc of peerConnectionsRef.current.values()) {
          const videoSender = pc.getSenders().find((s) => s.track?.kind === 'video');
          if (videoSender) {
            await videoSender.replaceTrack(screenTrack);
          } else if (localStreamRef.current) {
            pc.addTrack(screenTrack, localStreamRef.current);
          }
        }

        setScreenStream(capturedScreenStream);
        setIsScreenSharing(true);

        screenTrack.onended = async () => {
          for (const pc of peerConnectionsRef.current.values()) {
            const videoSender = pc.getSenders().find((s) => s.track?.kind === 'video');
            if (videoSender && originalCameraTrackRef.current) {
              await videoSender.replaceTrack(originalCameraTrackRef.current);
            }
          }
          setIsScreenSharing(false);
          setScreenStream(null);
        };
      } catch (err) {
        console.warn('[GroupWebRTC] Screen share cancelled or denied:', err);
      }
    }
  }, [isScreenSharing]);

  useEffect(() => {
    return () => {
      cleanupAll();
    };
  }, [cleanupAll]);

  return {
    localStream,
    remoteStreams,
    screenStream,
    isMuted,
    isCameraOff,
    isScreenSharing,
    initLocalMedia,
    connectToPeer,
    handleRemoteSignal,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    cleanupPeerConnection,
    cleanupAll,
  };
}
